/**
 * Perceptual quality metric for palette-based images (S-CIELAB).
 *
 * WHY THIS EXISTS
 * ---------------
 * /gif-compressor optimised exactly one number — bytes — for its whole life.
 * Every knob it owns (gifsicle --lossy, --colors, --resize-width, frame drop)
 * trades picture for size, but nothing in the tool could tell how much picture
 * a given trade cost, so the search happily returned settings that hit the
 * byte target through the most damaging route available. Measured on real
 * photographic GIFs, the ladder it used (`--lossy=200` + palette reduction)
 * was 11-14 VMAF points and ~1.5-2.5 ΔE worse than another setting of the SAME
 * output size. Bug report #1422: "из GIF делает пиксельное месево".
 *
 * WHY S-CIELAB AND NOT SSIM / PSNR / VMAF
 * ---------------------------------------
 *  - VMAF is computed on the LUMA plane only. A palette that collapses to grey
 *    still scores ~95 (measured: ffmpeg palettegen at 16 colours on foliage).
 *  - Plain per-pixel ΔE punishes dithering, which the eye integrates into the
 *    right colour — it would rank the worst option first.
 * S-CIELAB (Zhang & Wandell 1996) filters each opponent channel with the human
 * contrast sensitivity function BEFORE the colour difference, so dithering is
 * rewarded and banding / posterisation / lossy speckle are punished. That is
 * exactly the axis the complaint lives on.
 *
 * SCALE (mean ΔE over the frame, lower is better)
 *   < 1    indistinguishable
 *   1-2    visible only side by side
 *   2-3.5  noticeable but acceptable
 *   3.5-5  clearly degraded
 *   > 5    "pixel mush"
 *
 * Runs in the browser (ImageData) and in Node (plain {data,width,height}), so
 * the shipped code and the offline fixture battery score with the SAME code.
 */
(function (root, factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) module.exports = api;
    if (root) root.GifQuality = api;
}(typeof self !== 'undefined' ? self : typeof globalThis !== 'undefined' ? globalThis : null, function () {
    'use strict';

    // sRGB -> XYZ (D65)
    var M_XYZ = [0.4124564, 0.3575761, 0.1804375,
                 0.2126729, 0.7151522, 0.0721750,
                 0.0193339, 0.1191920, 0.9503041];
    // XYZ -> opponent (Zhang & Wandell)
    var M_OPP = [0.2790, 0.7200, -0.1070,
                -0.4490, 0.2900, -0.0770,
                 0.0860, -0.5900, 0.5010];
    // inverse of M_OPP
    var M_OPP_INV = (function invert(m) {
        var a = m[0], b = m[1], c = m[2], d = m[3], e = m[4], f = m[5], g = m[6], h = m[7], i = m[8];
        var A = e * i - f * h, B = -(d * i - f * g), C = d * h - e * g;
        var det = a * A + b * B + c * C;
        return [A / det, -(b * i - c * h) / det, (b * f - c * e) / det,
                B / det, (a * i - c * g) / det, -(a * f - c * d) / det,
                C / det, -(a * h - b * g) / det, (a * e - b * d) / det];
    }(M_OPP));

    // (weight, spread in degrees of visual angle) per opponent channel
    var FILTERS = [
        [[0.921, 0.0283], [0.105, 0.133], [-0.108, 0.4844]],
        [[0.531, 0.0392], [0.330, 0.494]],
        [[0.488, 0.0536], [0.371, 0.386]]
    ];

    // sRGB byte -> linear, precomputed (the metric's hottest lookup)
    var S2L = new Float32Array(256);
    for (var _i = 0; _i < 256; _i++) {
        var _v = _i / 255;
        S2L[_i] = _v <= 0.04045 ? _v / 12.92 : Math.pow((_v + 0.055) / 1.055, 2.4);
    }

    function blur1d(src, dst, w, h, sigma, horizontal) {
        var radius = Math.max(1, Math.ceil(sigma * 3));
        var k = new Float32Array(radius * 2 + 1), sum = 0;
        for (var t = -radius; t <= radius; t++) {
            var v = Math.exp(-(t * t) / (2 * sigma * sigma));
            k[t + radius] = v; sum += v;
        }
        for (var j = 0; j < k.length; j++) k[j] /= sum;
        var x, y, i2, acc, o;
        if (horizontal) {
            for (y = 0; y < h; y++) {
                var row = y * w;
                for (x = 0; x < w; x++) {
                    acc = 0;
                    for (i2 = -radius; i2 <= radius; i2++) {
                        o = x + i2; if (o < 0) o = 0; else if (o >= w) o = w - 1;
                        acc += src[row + o] * k[i2 + radius];
                    }
                    dst[row + x] = acc;
                }
            }
        } else {
            for (y = 0; y < h; y++) {
                for (x = 0; x < w; x++) {
                    acc = 0;
                    for (i2 = -radius; i2 <= radius; i2++) {
                        o = y + i2; if (o < 0) o = 0; else if (o >= h) o = h - 1;
                        acc += src[o * w + x] * k[i2 + radius];
                    }
                    dst[y * w + x] = acc;
                }
            }
        }
    }

    /**
     * RGBA bytes -> CSF-filtered CIELAB planes.
     * @param {{data:Uint8ClampedArray|Uint8Array,width:number,height:number}} img
     * @param {number} ppd pixels per degree of visual angle (≈30 for a screen at
     *        arm's length; the value only sets the filter widths)
     */
    function toFilteredLab(img, ppd) {
        var w = img.width, h = img.height, n = w * h, d = img.data;
        var o0 = new Float32Array(n), o1 = new Float32Array(n), o2 = new Float32Array(n);
        for (var p = 0, q = 0; p < n; p++, q += 4) {
            var r = S2L[d[q]], g = S2L[d[q + 1]], b = S2L[d[q + 2]];
            var X = M_XYZ[0] * r + M_XYZ[1] * g + M_XYZ[2] * b;
            var Y = M_XYZ[3] * r + M_XYZ[4] * g + M_XYZ[5] * b;
            var Z = M_XYZ[6] * r + M_XYZ[7] * g + M_XYZ[8] * b;
            o0[p] = M_OPP[0] * X + M_OPP[1] * Y + M_OPP[2] * Z;
            o1[p] = M_OPP[3] * X + M_OPP[4] * Y + M_OPP[5] * Z;
            o2[p] = M_OPP[6] * X + M_OPP[7] * Y + M_OPP[8] * Z;
        }
        var planes = [o0, o1, o2];
        var tmp = new Float32Array(n), tmp2 = new Float32Array(n);
        for (var c = 0; c < 3; c++) {
            var src = planes[c];
            var acc = new Float32Array(n);
            for (var fi = 0; fi < FILTERS[c].length; fi++) {
                var weight = FILTERS[c][fi][0];
                var sigma = Math.max(0.35, FILTERS[c][fi][1] * ppd / 2);
                blur1d(src, tmp, w, h, sigma, true);
                blur1d(tmp, tmp2, w, h, sigma, false);
                for (var m = 0; m < n; m++) acc[m] += weight * tmp2[m];
            }
            planes[c] = acc;
        }
        // opponent -> XYZ -> Lab
        var L = new Float32Array(n), A = new Float32Array(n), B = new Float32Array(n);
        var wx = 0.95047, wy = 1.0, wz = 1.08883, eps = 216 / 24389, kk = 24389 / 27;
        for (var z = 0; z < n; z++) {
            var a0 = planes[0][z], a1 = planes[1][z], a2 = planes[2][z];
            var X2 = M_OPP_INV[0] * a0 + M_OPP_INV[1] * a1 + M_OPP_INV[2] * a2;
            var Y2 = M_OPP_INV[3] * a0 + M_OPP_INV[4] * a1 + M_OPP_INV[5] * a2;
            var Z2 = M_OPP_INV[6] * a0 + M_OPP_INV[7] * a1 + M_OPP_INV[8] * a2;
            var fx = X2 > 0 ? X2 / wx : 0, fy = Y2 > 0 ? Y2 / wy : 0, fz = Z2 > 0 ? Z2 / wz : 0;
            fx = fx > eps ? Math.cbrt(fx) : (kk * fx + 16) / 116;
            fy = fy > eps ? Math.cbrt(fy) : (kk * fy + 16) / 116;
            fz = fz > eps ? Math.cbrt(fz) : (kk * fz + 16) / 116;
            L[z] = 116 * fy - 16;
            A[z] = 500 * (fx - fy);
            B[z] = 200 * (fy - fz);
        }
        return { L: L, A: A, B: B, n: n };
    }

    var RAD = Math.PI / 180, DEG = 180 / Math.PI;
    var P25_7 = Math.pow(25, 7);

    function de2000(L1, a1, b1, L2, a2, b2) {
        var C1 = Math.sqrt(a1 * a1 + b1 * b1), C2 = Math.sqrt(a2 * a2 + b2 * b2);
        var Cb = (C1 + C2) / 2, Cb7 = Math.pow(Cb, 7);
        var G = 0.5 * (1 - Math.sqrt(Cb7 / (Cb7 + P25_7 + 1e-12)));
        var a1p = (1 + G) * a1, a2p = (1 + G) * a2;
        var C1p = Math.sqrt(a1p * a1p + b1 * b1), C2p = Math.sqrt(a2p * a2p + b2 * b2);
        var h1p = (Math.atan2(b1, a1p) * DEG + 360) % 360;
        var h2p = (Math.atan2(b2, a2p) * DEG + 360) % 360;
        var dLp = L2 - L1, dCp = C2p - C1p;
        var dhp = h2p - h1p;
        if (dhp > 180) dhp -= 360; else if (dhp < -180) dhp += 360;
        if (C1p * C2p === 0) dhp = 0;
        var dHp = 2 * Math.sqrt(C1p * C2p) * Math.sin(dhp / 2 * RAD);
        var Lbp = (L1 + L2) / 2, Cbp = (C1p + C2p) / 2;
        var hsum = h1p + h2p, hbp;
        if (C1p * C2p === 0) hbp = hsum;
        else if (Math.abs(h1p - h2p) <= 180) hbp = hsum / 2;
        else hbp = hsum < 360 ? (hsum + 360) / 2 : (hsum - 360) / 2;
        var T = 1 - 0.17 * Math.cos((hbp - 30) * RAD) + 0.24 * Math.cos(2 * hbp * RAD)
            + 0.32 * Math.cos((3 * hbp + 6) * RAD) - 0.20 * Math.cos((4 * hbp - 63) * RAD);
        var dth = 30 * Math.exp(-Math.pow((hbp - 275) / 25, 2));
        var Cbp7 = Math.pow(Cbp, 7);
        var Rc = 2 * Math.sqrt(Cbp7 / (Cbp7 + P25_7 + 1e-12));
        var Sl = 1 + (0.015 * (Lbp - 50) * (Lbp - 50)) / Math.sqrt(20 + (Lbp - 50) * (Lbp - 50));
        var Sc = 1 + 0.045 * Cbp, Sh = 1 + 0.015 * Cbp * T;
        var Rt = -Math.sin(2 * dth * RAD) * Rc;
        var x = dLp / Sl, y = dCp / Sc, z = dHp / Sh;
        return Math.sqrt(x * x + y * y + z * z + Rt * y * z);
    }

    /**
     * Mean gradient magnitude of the luma plane — a cheap stand-in for "how
     * much fine detail is in this picture".
     *
     * S-CIELAB deliberately blurs both images through the eye's contrast
     * sensitivity function before comparing, which is what makes it fair to
     * dithering. The same property makes it nearly blind to BLUR: a downscaled
     * candidate scores well because the reference got softened too. Measured
     * consequence — on dense foliage at a quarter of the original size the ΔE
     * ranking preferred a 56 % downscale that VMAF scored 7.5 points WORSE than
     * the alternative. Detail loss is the axis ΔE cannot see, so it is measured
     * separately and folded in by the caller.
     */
    function detailEnergy(img) {
        var w = img.width, h = img.height, d = img.data;
        if (w < 3 || h < 3) return 0;
        var sum = 0, n = 0;
        for (var y = 1; y < h - 1; y++) {
            for (var x = 1; x < w - 1; x++) {
                var i = (y * w + x) * 4;
                var c = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
                var r = 0.299 * d[i + 4] + 0.587 * d[i + 5] + 0.114 * d[i + 6];
                var b = 0.299 * d[i + w * 4] + 0.587 * d[i + w * 4 + 1] + 0.114 * d[i + w * 4 + 2];
                sum += Math.abs(r - c) + Math.abs(b - c);
                n++;
            }
        }
        return n ? sum / n : 0;
    }

    /**
     * Perceptual difference between two same-sized RGBA images.
     * @returns {{mean:number, p95:number, pctOver3:number, detailLoss:number}}
     *   detailLoss ∈ [0,1] — share of the reference's fine detail that the
     *   candidate no longer carries (0 = kept, 1 = flat).
     */
    function compare(refImg, distImg, opts) {
        opts = opts || {};
        var ppd = opts.ppd || 30;
        if (refImg.width !== distImg.width || refImg.height !== distImg.height) {
            throw new Error('GifQuality.compare: size mismatch');
        }
        var a = toFilteredLab(refImg, ppd), b = toFilteredLab(distImg, ppd);
        var n = a.n, sum = 0, over = 0;
        var vals = new Float32Array(n);
        for (var i = 0; i < n; i++) {
            var d = de2000(a.L[i], a.A[i], a.B[i], b.L[i], b.A[i], b.B[i]);
            vals[i] = d; sum += d; if (d > 3) over++;
        }
        var sorted = vals.slice().sort();
        var refDetail = detailEnergy(refImg);
        var distDetail = detailEnergy(distImg);
        return {
            mean: sum / n,
            p95: sorted[Math.min(n - 1, Math.floor(n * 0.95))],
            pctOver3: (over / n) * 100,
            detailLoss: refDetail > 0.5 ? Math.max(0, Math.min(1, 1 - distDetail / refDetail)) : 0
        };
    }

    return { compare: compare, detailEnergy: detailEnergy, toFilteredLab: toFilteredLab, deltaE2000: de2000 };
}));
